import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '@/contexts/CartContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import Button from '@/components/atoms/Button';
import Icon from '@/components/atoms/Icon/Icon';
import './StickyCartFooter.scss';

const StickyCartFooter: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { cart } = useCart();
  const { formatPrice } = useCurrency();

  // Check if we're on an event details page (with venue path)
  const isEventDetailsPage = location.pathname.match(/^\/events\/\d+\/venues\/\d+$/);

  // Manage body class for sticky cart footer spacing
  useEffect(() => {
    const hasItems = cart && cart.items && cart.items.length > 0;
    const shouldShowFooter = hasItems && isEventDetailsPage;
    
    if (shouldShowFooter) {
      document.body.classList.add('has-sticky-cart');
    } else {
      document.body.classList.remove('has-sticky-cart');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('has-sticky-cart');
    };
  }, [cart, isEventDetailsPage]);

  // Don't render if no cart, no items, or not on event details page
  if (!cart || !cart.items || cart.items.length === 0 || !isEventDetailsPage) {
    return null;
  }

  const totalItems = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  const handleViewCart = () => {
    navigate('/cart');
  };

  return (
    <div className="sticky-cart-footer">
      <div className="sticky-cart-footer__content">
        <div className="sticky-cart-footer__info">
          <div className="sticky-cart-footer__items">
            <Icon name="carrito" size={20} alt={t('cart.title')} />
            <span className="sticky-cart-footer__count">
              {totalItems} = {formatPrice(cart.total_amount)}
            </span>
          </div>
        </div>
        <Button 
          variant="primary" 
          size="sm"
          onClick={handleViewCart}
          className="sticky-cart-footer__button"
        >
          {t('cart.viewCart')}
        </Button>
      </div>
    </div>
  );
};

export default StickyCartFooter;
